import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

export default function TrendVideoSocialPage() {
  const navigate  = useNavigate();
  const [videos,  setVideos]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [copied,  setCopied]  = useState(null); // id of last copied

  const fetchVideos = useCallback(async () => {
    try {
      const res = await fetch('/api/trend-video-social', { credentials: 'include' });
      if (res.status === 401 || res.status === 403) {
        navigate('/trend-spotter');
        return;
      }
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to load');
      setVideos(data.videos);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const handleCopy = async (video) => {
    try {
      await navigator.clipboard.writeText(video.caption || '');
      setCopied(video.id);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // fallback
      const el = document.createElement('textarea');
      el.value = video.caption || '';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(video.id);
      setTimeout(() => setCopied(null), 2000);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this trend video?')) return;
    try {
      const res = await fetch(`/api/trend-video-social/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) setVideos(v => v.filter(x => x.id !== id));
    } catch (e) {
      alert('Delete failed: ' + e.message);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    return bytes > 1024 * 1024
      ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
      : `${Math.round(bytes / 1024)} KB`;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0f0f0f', color: '#fff', fontFamily: 'sans-serif' }}>

      {/* Header */}
      <div style={{ background: '#1a1a1a', borderBottom: '1px solid #2a2a2a', padding: '16px 24px',
                    display: 'flex', alignItems: 'center', gap: 16 }}>
        <button
          onClick={() => navigate('/admin')}
          style={{ background: 'none', border: '1px solid #444', color: '#aaa', borderRadius: 6,
                   padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}
        >
          ← Admin
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Trend Video Social</h1>
          <p style={{ margin: 0, fontSize: 13, color: '#888', marginTop: 2 }}>
            Auto-generated 10s template videos for approved trends
          </p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#666' }}>{videos.length} videos</span>
          <button
            onClick={fetchVideos}
            style={{ background: '#2a2a2a', border: '1px solid #444', color: '#ccc', borderRadius: 6,
                     padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: 24 }}>
        {loading && (
          <div style={{ textAlign: 'center', color: '#666', padding: 60 }}>Loading videos…</div>
        )}
        {error && (
          <div style={{ color: '#f87171', textAlign: 'center', padding: 40 }}>{error}</div>
        )}
        {!loading && !error && videos.length === 0 && (
          <div style={{ textAlign: 'center', color: '#555', padding: 80 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🎬</div>
            <div style={{ fontSize: 16, marginBottom: 8 }}>No trend videos yet</div>
            <div style={{ fontSize: 13, color: '#444' }}>
              The workflow generates a video automatically when a trend is approved.
              <br />Run <code style={{ color: '#a78bfa' }}>trend_video_workflow.py</code> to generate now.
            </div>
          </div>
        )}

        {/* Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
          gap: 24,
        }}>
          {videos.map(v => (
            <div key={v.id} style={{
              background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 12,
              overflow: 'hidden', display: 'flex', flexDirection: 'column',
            }}>
              {/* Video player */}
              <div style={{ background: '#000', aspectRatio: '9/16', maxHeight: 420, position: 'relative' }}>
                <video
                  controls
                  playsInline
                  style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                  src={`/api/trend-video-social/video/${v.id}`}
                />
              </div>

              {/* Info */}
              <div style={{ padding: 16, flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Trend name + meta */}
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15, lineHeight: 1.4, marginBottom: 4 }}>
                    {v.trend_name}
                  </div>
                  <div style={{ fontSize: 12, color: '#555', display: 'flex', gap: 8 }}>
                    <span>{formatDate(v.created_at)}</span>
                    {v.video_bytes && <span>·</span>}
                    {v.video_bytes && <span>{formatSize(v.video_bytes)}</span>}
                    <span>·</span>
                    <span style={{
                      color: v.status === 'ready' ? '#4ade80' : '#f87171',
                    }}>{v.status}</span>
                  </div>
                </div>

                {/* Caption */}
                {v.caption && (
                  <div>
                    <div style={{ fontSize: 11, color: '#555', fontWeight: 600, textTransform: 'uppercase',
                                  letterSpacing: '0.05em', marginBottom: 6 }}>
                      Caption
                    </div>
                    <div style={{
                      background: '#111', border: '1px solid #2a2a2a', borderRadius: 8,
                      padding: '10px 12px', fontSize: 12.5, color: '#ccc', lineHeight: 1.6,
                      whiteSpace: 'pre-wrap', maxHeight: 180, overflowY: 'auto',
                    }}>
                      {v.caption}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
                  <button
                    onClick={() => handleCopy(v)}
                    style={{
                      flex: 1, padding: '8px 0', borderRadius: 7, border: 'none',
                      background: copied === v.id ? '#166534' : '#7c3aed',
                      color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                      transition: 'background 0.2s',
                    }}
                  >
                    {copied === v.id ? '✓ Copied!' : 'Copy Caption'}
                  </button>
                  <button
                    onClick={() => handleDelete(v.id)}
                    style={{
                      padding: '8px 14px', borderRadius: 7, border: '1px solid #3a1a1a',
                      background: '#1a0a0a', color: '#f87171', fontSize: 13, cursor: 'pointer',
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
